import type { ReactElement } from 'react';
import * as Sentry from '@sentry/nextjs';
import { type GetStaticPaths, type GetStaticProps } from 'next';
import type { NextPageWithLayout } from 'types/page';
import MainLayout from 'layouts/MainLayout';
import { AMENITY_LABELS } from 'constants/venues/profileOptions';
import { CourtSurfacesEnum, VenueAccessTypesEnum, VenueNetsEnum } from 'types/generated/server';
import { getAllVenuePaths } from 'services/server/graphql/queries/getAllVenuePaths';
import { getVenueBySlug } from 'services/server/graphql/queries/getVenueBySlug';
import { getLatestBlogPosts } from 'services/server/blog/getLatestBlogPosts';
import { generateBreadcrumbListJsonLd } from 'utils/shared/json-ld/breadcrumbs';
import { combineJsonLdSchemas } from 'utils/shared/json-ld/combineJsonLdSchemas';
import { generateFaqJsonLd } from 'utils/shared/json-ld/faq';
import { generateLocalBusinessJsonLd } from 'utils/shared/json-ld/localBusiness';
import { PostalAddress } from 'utils/shared/json-ld/types';
import CourtPage, { Props } from 'screens/CourtPageV2';
import CityLocationProvider from 'components/utilities/CityLocationProvider';

/**
 * @note it's unclear if this is an option in Vercel so testing if it works
 * "Optional but recommended: use the Edge Runtime. This can only be done at the page level, not inside nested components."
 */
/**
 * @todo When comparing staging to production, prod was actually faster without this, but I'm unusure if it's the root cause. More analysis needed.
 */
// export const runtime = 'experimental-edge';

const REVALIDATE_IN_SECONDS = process.env.TARGET_PLATFORM === 'mobile' ? undefined : 30 * 60 * 1;
const IS_ALL_AS_ISR = true;

interface FaqProps {
  venue: Awaited<ReturnType<typeof getVenueBySlug>>['venues'][0];
}

const mapSurfaceToName = {
  [CourtSurfacesEnum.Acrylic]: 'acrylic',
  [CourtSurfacesEnum.Asphalt]: 'asphalt',
  [CourtSurfacesEnum.Clay]: 'clay',
  [CourtSurfacesEnum.Carpet]: 'carpet',
  [CourtSurfacesEnum.Concrete]: 'concrete',
  [CourtSurfacesEnum.Grass]: 'grass',
  [CourtSurfacesEnum.Hard]: 'hard',
  [CourtSurfacesEnum.Wood]: 'wood',
};

const generateFaqs = ({ venue }: FaqProps) => {
  const faqs: { question: string; answer: string }[] = [];

  if (venue.hasPickleball) {
    faqs.push({
      question: `Can I play pickleball at ${venue.title}?`,
      answer: `Yes, ${venue.title} has pickleball courts.`,
    });
  }
  if (venue.hasTennis) {
    faqs.push({
      question: `Can I play tennis at ${venue.title}?`,
      answer: `Yes, ${venue.title} has tennis courts.`,
    });
  }

  if (venue.hasPadel) {
    faqs.push({
      question: `Can I play padel at ${venue.title}?`,
      answer: `Yes, ${venue.title} has padel courts.`,
    });
  }

  if (!!venue.accessType) {
    if (venue.accessType === VenueAccessTypesEnum.Free) {
      faqs.push({
        question: `Is it free to play at ${venue.title}?`,
        answer: `You can play for free at ${venue.title}.`,
      });
    } else {
      if (venue.accessType === VenueAccessTypesEnum.Membership) {
        faqs.push({
          question: `Does it cost money to play at ${venue.title}?`,
          answer: `Playing at ${venue.title} requires a membership.`,
        });
      } else if (venue.accessType === VenueAccessTypesEnum.OneTime) {
        faqs.push({
          question: `Does it cost money to play at ${venue.title}?`,
          answer: `Playing at ${venue.title} requires a one-time fee to access the courts.`,
        });
      } else if (venue.accessType === VenueAccessTypesEnum.Private) {
        faqs.push({
          question: `Does it cost money to play at ${venue.title}?`,
          answer: `${venue.title} is a private club.`,
        });
      }
    }
  }

  if (venue.indoorCourtCount || venue.outdoorCourtCount) {
    faqs.push({
      question: `How many courts are at ${venue.title}?`,
      answer: `${venue.title} has ${venue.indoorCourtCount || 0} indoor courts and ${
        venue.outdoorCourtCount || 0
      } outdoor courts. There are a total of ${
        venue.indoorCourtCount + venue.outdoorCourtCount
      } courts.`,
    });
  }

  if (venue.pickleballNets) {
    if (venue.pickleballNets === VenueNetsEnum.Permanent) {
      faqs.push({
        question: `Are there dedicated pickleball courts?`,
        answer: `${venue.title} has dedicated and permanent pickleball courts.`,
      });
    } else if (venue.pickleballNets === VenueNetsEnum.Tennis) {
      faqs.push({
        question: `Are there dedicated pickleball courts?`,
        answer: `${venue.title} uses tennis courts.`,
      });
    } else if (venue.pickleballNets === VenueNetsEnum.Portable) {
      faqs.push({
        question: `Are there dedicated pickleball courts?`,
        answer: `${venue.title} uses portable nets.`,
      });
    } else if (venue.pickleballNets === VenueNetsEnum.BringYourOwn) {
      faqs.push({
        question: `Are there dedicated pickleball courts?`,
        answer: `You must bring your own nets to ${venue.title}.`,
      });
    }
  }

  if (venue.courtSurfaces && venue.courtSurfaces.length > 0) {
    const surfaces: string[] = [];

    venue.courtSurfaces.forEach((surface) => {
      surfaces.push(mapSurfaceToName[surface.courtSurface]);
    });

    faqs.push({
      question: `What are the court surfaces at ${venue.title}?`,
      answer: `Surfaces: ${surfaces.join(', ')}.`,
    });
  }

  if (venue.addressString) {
    faqs.push({
      question: `Where is ${venue.title} located?`,
      answer: `${venue.title} is located at ${venue.addressString}.`,
    });
  }

  if (venue.email || venue.phoneNumber || venue.websiteUrl) {
    const contactInfo: string[] = [];

    if (venue.email) {
      contactInfo.push(`Email: ${venue.email}`);
    }

    if (venue.phoneNumber) {
      contactInfo.push(`Phone: ${venue.phoneNumber}`);
    }

    if (venue.websiteUrl) {
      contactInfo.push(`Website: ${venue.websiteUrl}`);
    }

    faqs.push({
      question: `How can I contact ${venue.title}?`,
      answer: contactInfo.join(', '),
    });
  }

  if (venue.amenities && venue.amenities.length > 0) {
    const amenities = venue.amenities
      .map((amenity) => AMENITY_LABELS[amenity.amenity]?.toLowerCase())
      .filter(Boolean);

    if (amenities.length > 0) {
      faqs.push({
        question: `What amenities does ${venue.title} have?`,
        answer: `Amenities: ${amenities.join(', ')}.`,
      });
    }
  }

  // if (venue.description) {
  //   faqs.push({
  //     question: `What is ${venue.title}?`,
  //     answer: venue.description,
  //   });
  // }

  faqs.push({
    question: `How do I play at ${venue.title}?`,
    answer: `You can play at ${venue.title} by joining or creating an open play through Bounce.`,
  });

  return faqs;
};

export const getStaticProps: GetStaticProps<Props, { venueSlug: string }> = async ({ params }) => {
  const venueSlug = params?.venueSlug;

  if (!venueSlug) {
    return {
      notFound: true,
      revalidate: REVALIDATE_IN_SECONDS,
    };
  }

  try {
    const [venueResponse, blogPosts] = await Promise.all([
      getVenueBySlug({ slug: venueSlug }),
      getLatestBlogPosts(3)
        .then(({ posts }) => posts)
        .catch((error) => {
          Sentry.captureException(error);
          return [];
        }),
    ]);
    const venue = venueResponse?.venues[0];

    if (!venue || !venue?.id || !venue?.title) {
      return {
        notFound: true,
        revalidate: REVALIDATE_IN_SECONDS,
      };
    }

    // Prefer the club's own FAQs once they've set them up in the DB; otherwise
    // fall back to the auto-generated (hardcoded) set.
    const dynamicFaqs =
      venue.faqs?.map((faq) => ({ question: faq.question, answer: faq.answer })) ?? [];
    const faqs = dynamicFaqs.length > 0 ? dynamicFaqs : generateFaqs({ venue });

    const postalAddress: PostalAddress = {
      ...(venue.addressString && { streetAddress: venue.addressString }),
      ...(venue.city?.name && { addressLocality: venue.city.name }),
      ...(venue.city?.countrySubdivision?.name && {
        addressRegion: venue.city.countrySubdivision.name,
      }),
      ...(venue.city?.countrySubdivision?.country?.id && {
        addressCountry: venue.city.countrySubdivision.country.id.toUpperCase(),
      }),
    };
    const hasStructuredAddress = Object.keys(postalAddress).length > 0;

    const venueSchema = generateLocalBusinessJsonLd({
      name: venue.title,
      additionalTypes: ['SportsActivityLocation'],
      telephone: venue.phoneNumber,
      url: venue.websiteUrl,
      image: venue?.images?.[0]?.url || '',
      geo: {
        latitude: venue.latitude,
        longitude: venue.longitude,
      },
      address: hasStructuredAddress ? postalAddress : venue.addressString,
    });
    const faqSchema = generateFaqJsonLd({
      items: faqs,
    });
    /**
     * @note Can't do breadcrumbs because courts can have multiple sports.
     */
    // const breadcrumbs = generateBreadcrumbListJsonLd({
    //   items: generateBreadcrumbs({
    //     venueTitle: venue.title,
    //     venueSlug: venue?.slug || '',
    //     cityName: venue?.city?.name || '',
    //     citySlug: venue?.city?.slug || '',
    //     regionName: venue?.city?.countrySubdivision?.name || '',
    //     regionSlug: venue?.city?.countrySubdivision?.slug || '',
    //     countryId: venue?.city?.countrySubdivision?.country?.id || '',
    //     countryName: venue?.city?.countrySubdivision?.country?.name || '',
    //     countrySlug: venue?.city?.countrySubdivision?.country?.slug || '',
    //     // TODO: Court pages should eventually be sport-specific (e.g., /tennis/court/[venueSlug])
    //     // For now, defaulting to 'pickleball' for backward compatibility since the system originally assumed pickleball
    //     // sport: 'pickleball',
    //   }).jsonLd,
    // });

    const combinedSchemas = combineJsonLdSchemas([
      venueSchema,
      faqSchema,
      // breadcrumbs,
    ]);

    return {
      props: {
        venue,
        faqs: faqs,
        jsonLd: JSON.stringify(combinedSchemas),
        followersCount: venueResponse?.followers?.aggregate?.count ?? 0,
        blogPosts,
      },
      revalidate: REVALIDATE_IN_SECONDS,
    };
  } catch (error) {
    Sentry.captureException(error);
    return {
      notFound: true,
      revalidate: REVALIDATE_IN_SECONDS,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (IS_ALL_AS_ISR) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const venues = await getAllVenuePaths();

  const paths = venues.venues.map((venue) => {
    return {
      params: {
        venueSlug: venue.slug,
      },
    };
  });

  return {
    paths,
    fallback: 'blocking',
  };
};

const CourtPageWithProvider: NextPageWithLayout<Props> = (props) => {
  const city = props.venue?.city;

  // Only wrap with CityLocationProvider if we have city data
  if (city && city.latitude && city.longitude) {
    const cityData = {
      ...city,
      state: city.countrySubdivision?.name || '',
      country: city.countrySubdivision?.country?.id || '',
    };

    return (
      <CityLocationProvider city={cityData}>
        <CourtPage {...props} />
      </CityLocationProvider>
    );
  }

  // Fallback to regular CourtPage if no city data
  return <CourtPage {...props} />;
};

// MainLayout defaults (top nav, no sidebar) match what the screen used to
// hand-wire via SafeAreaPage; no mainClassName — the screen manages its own
// widths so the breadcrumbs bar can run full-bleed.
CourtPageWithProvider.getLayout = (page: ReactElement) => <MainLayout>{page}</MainLayout>;

export default CourtPageWithProvider;
